import React from "react";
import { Box, Paper, Container } from "@mui/material";
import styled from "styled-components/macro";
import { Trans } from "react-i18next";
import { useOpenAssistant } from "components/assistant/assistantHooks";
import { PracticeIcon } from "./PracticeIcon";
import { practiceOptions, PracticeOption } from "./practiceOptions";
import { HeaderLayout } from "components/header/Header";
import TutorialPointer from "root/tutorial/TutorialPointer";
import { useIsQuestListenVocab } from "root/main/main/quests/ListenVocabQuest";
import { useIsQuestAiAssistant } from "root/main/main/quests/AiAssistantQuest";
import { AssistantMode } from "components/assistant/__generated__/AssistantMutation.graphql";
import { canTts } from "queries/tts/ttsTools";
import { useViewerDecks } from "queries/viewerQuery";

const PracticeCard = styled(Paper)`
  position: relative;
  padding: 20px 18px;
  margin-bottom: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 20px;
  border-radius: 10px;
  background: ${({ theme }) => theme.duo.color.white};
  border: 1px solid ${({ theme }) => theme.duo.color.secondLevelBg};
`;
const Content = styled.div`
  flex: 1;
`;
const CardTitle = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: ${({ theme }) => theme.duo.color.text};
  margin-bottom: 4px;
  letter-spacing: 0.2px;
  text-transform: uppercase;
`;
const Description = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.duo.palette.greyDark};
  line-height: 1.35;
`;
const Title = styled.span`
  font-weight: bold;
  font-size: 90%;
`;

const STutorialPointer = styled(TutorialPointer)`
  position: absolute;
  right: 20%;
  transform: translate(-50%, -50%);
`;

const Practice: React.FC = () => {
  const { deck } = useViewerDecks();
  const openAssistant = useOpenAssistant();
  const questListenVocabActive = useIsQuestListenVocab();
  const questAiAssistantActive = useIsQuestAiAssistant();
  const listenVocabSupported = canTts(deck?.front);

  const handlePracticeClick = (option: PracticeOption) => {
    openAssistant({
      autoApplyMode: option.mode
    });
  };

  const showQuestPointerForMode = (mode: AssistantMode) => {
    if (mode === "listenVocab") return questListenVocabActive;
    if (mode === "assistant") return questAiAssistantActive;
    return false;
  };

  const isVisible = React.useCallback(
    (mode: AssistantMode) => {
      if (mode === "aiStory" || mode === "cardDetail") return false;
      if (mode === "listenVocab") return listenVocabSupported;
      return true;
    },
    [listenVocabSupported]
  );

  return (
    <HeaderLayout noBack title={<Trans parent={Title}>PRACTICE</Trans>}>
      <Container maxWidth="sm" sx={{ p: 2 }}>
        <Box>
          {practiceOptions
            .filter((option) => isVisible(option.mode))
            .map((option) => (
              <PracticeCard key={option.title} id={option.mode} onClick={() => handlePracticeClick(option)}>
                <PracticeIcon icon={option.icon} size="default" />
                <Content>
                  <CardTitle>
                    <Trans>{option.title}</Trans>
                  </CardTitle>
                  <Description>
                    <Trans>{option.description}</Trans>
                  </Description>
                </Content>
                {showQuestPointerForMode(option.mode) && (
                  <STutorialPointer pulsing container={() => document.getElementById(option.mode)} />
                )}
              </PracticeCard>
            ))}
        </Box>
      </Container>
    </HeaderLayout>
  );
};

export default Practice;
